import { useState, type CSSProperties } from 'react'
import { CalendarClock } from 'lucide-react'
import { buttonClass, ButtonGlyph, HelpIcon, Link, LoadFailure, Skeleton } from '../../../core/index.js'
import { useFacilityHeatmap, useFacilityHeatmapHistory } from '../../hooks.js'
import type { FacilityHeatmapView, HeatmapDay, HeatmapHistoryMonth } from '../../types.js'
import { HeatLegend } from './HeatLegend.js'
import { calendarOf, heatPaint, HEAT_ORDER, HEAT_SWATCH, pctOf, ratioState, shiftBars, SHIFT_LABEL, SHIFT_ORDER, tallySlots, timeOfDay, type HeatPaint } from './heatmapData.js'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const paintStyle = (paint: HeatPaint): CSSProperties => ({ background: paint.background, color: paint.color, border: paint.border })

/** "Thursday, October 15". */
const longDay = (date: string): string => new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' })
/** "Oct 2026". */
const shortMonth = (period: string): string => new Date(`${period}-01T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })

function Tile({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="flex min-w-0 flex-1 basis-36 flex-col gap-0.5 rounded border border-marine/15 px-3 py-2">
      <span className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{label}</span>
      <span className="text-xl font-black text-ink">{value}</span>
      <span className="text-[11px] text-ink-mid">{note}</span>
    </div>
  )
}

function DayCell({ day, selected, onSelect, scale }: { day: HeatmapDay; selected: boolean; onSelect: () => void; scale: FacilityHeatmapView['scale'] }) {
  const paint = heatPaint(day.state, timeOfDay(day.phase))
  const bars = shiftBars(day.slots, scale)
  const date = Number(day.date.slice(8))
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      aria-label={`${longDay(day.date)}: ${HEAT_SWATCH[day.state].label}${day.required > 0 ? `, ${day.staffed} of ${day.required} seats` : ''}`}
      className={`flex min-h-[4.75rem] flex-col justify-between rounded-md px-1.5 py-1 text-left ${selected ? 'outline outline-[3px] outline-offset-2 outline-coral' : ''}`}
      style={{ ...paintStyle(paint), ...(day.phase === 'today' ? { border: '3px solid #0A2830' } : {}) }}
    >
      <span className="flex w-full items-baseline justify-between gap-1">
        <span className="text-sm font-black">{date}</span>
        {day.required > 0 && <span className="text-[10px] font-bold">{day.staffed}/{day.required}</span>}
      </span>
      {day.phase === 'today' && <span className="self-start rounded bg-ink px-1 text-[9px] font-bold text-white">Today</span>}
      <span aria-hidden="true" className="grid w-full grid-cols-3 gap-0.5">
        {bars.map((bar) => (
          <span key={bar.shiftType} className="h-1.5 rounded-sm" style={{ background: HEAT_SWATCH[bar.state].fill, boxShadow: `0 0 0 1px ${paint.color === '#FFFFFF' ? 'rgba(255,255,255,.5)' : 'rgba(10,40,48,.18)'}` }} />
        ))}
      </span>
    </button>
  )
}

function DayDetail({ day, view, todayHref }: { day: HeatmapDay; view: FacilityHeatmapView; todayHref: string }) {
  const paint = heatPaint(day.state, timeOfDay(day.phase))
  const unitName = (unitId: string) => view.units.find((unit) => unit.id === unitId)?.name ?? unitId
  const units = [...new Set(day.slots.map((slot) => slot.unitId))]
  const shiftTypes = SHIFT_ORDER.filter((key) => day.slots.some((slot) => slot.shiftType === key))
  const phase = day.phase === 'worked' ? 'Worked' : day.phase === 'today' ? 'Today · under way' : 'Scheduled · the board is still filling it'
  const counted = day.slots.filter((slot) => slot.census !== null)
  const census = counted.reduce((sum, slot, index, all) => (all.findIndex((other) => other.unitId === slot.unitId) === index ? sum + (slot.census ?? 0) : sum), 0)
  const recorded = counted.length > 0 && counted.every((slot) => slot.censusRecorded)
  return (
    <div className="flex flex-col gap-3">
      <div>
        <span className="text-[11px] font-semibold text-ink-mid">{phase}</span>
        <h3 className="text-base font-black text-ink">{longDay(day.date)}</h3>
      </div>
      <div className="flex items-center gap-3 rounded-md px-3 py-2" style={paintStyle(paint)}>
        <span className="text-lg font-black">{HEAT_SWATCH[day.state].label}</span>
        <span className="text-xs font-semibold">{day.required > 0 ? `${day.staffed} of ${day.required} seats · ${pctOf(day.staffed, day.required)}` : 'Nothing required'}</span>
      </div>
      {units.length > 0 && (
        <div className="grid gap-1 text-[11px]" style={{ gridTemplateColumns: `5.5rem repeat(${shiftTypes.length}, minmax(0, 1fr))` }}>
          <span />
          {shiftTypes.map((shiftType) => (
            <span key={shiftType} className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">
              {SHIFT_LABEL[shiftType]}
            </span>
          ))}
          {units.map((unitId) => (
            <div key={unitId} className="contents">
              <span className="self-center font-bold text-ink">{unitName(unitId)}</span>
              {shiftTypes.map((shiftType) => {
                const slots = day.slots.filter((slot) => slot.unitId === unitId && slot.shiftType === shiftType)
                const tally = tallySlots(slots)
                const state = ratioState(tally.staffed, tally.required, view.scale)
                return (
                  <span key={shiftType} className="flex h-10 items-center justify-center rounded text-xs font-black" style={paintStyle(heatPaint(state, timeOfDay(day.phase)))} title={slots.map((slot) => `${slot.role} ${slot.staffed}/${slot.required}`).join(' · ')}>
                    {tally.required > 0 ? `${tally.staffed}/${tally.required}` : '—'}
                  </span>
                )
              })}
            </div>
          ))}
        </div>
      )}
      <ul className="flex flex-col gap-1 text-[11px] leading-snug text-ink-mid">
        {counted.length > 0 && <li>{`Census ${census} ${recorded ? 'recorded' : 'forecast'}.`}</li>}
        {day.ranShort > 0 && <li>{`${day.ranShort} seat${day.ranShort === 1 ? '' : 's'} run short by staff.`}</li>}
        {day.uncovered > 0 && <li>{`${day.uncovered} seat${day.uncovered === 1 ? '' : 's'} ${day.phase === 'worked' ? 'went unfilled' : 'not on the board'}.`}</li>}
        {day.onBoard > 0 && <li>{`${day.onBoard} seat${day.onBoard === 1 ? '' : 's'} on the board now.`}</li>}
        {day.required > 0 && day.staffed >= day.required && <li>Every seat the census requires {day.phase === 'worked' ? 'was worked' : 'is held'}.</li>}
      </ul>
      {day.phase === 'today' && (
        <Link to={todayHref} className={`${buttonClass({ size: 'sm' })} self-start`}>
          <ButtonGlyph icon={CalendarClock} size="sm" />
          Open today
        </Link>
      )}
    </div>
  )
}

function HistoryStrip({ months, current, onChoose }: { months: readonly HeatmapHistoryMonth[]; current: string; onChoose: (period: string) => void }) {
  return (
    <div className="overflow-x-auto">
      <div className="flex min-w-[44rem] flex-col gap-1">
        {[...months].reverse().map((month) => {
          const [year, mon] = month.period.split('-').map(Number)
          const length = new Date(Date.UTC(year ?? 1970, mon ?? 1, 0)).getUTCDate()
          const chosen = month.period === current
          return (
            <button key={month.period} type="button" onClick={() => onChoose(month.period)} disabled={!month.released} aria-label={`${shortMonth(month.period)}: ${month.released ? HEAT_SWATCH[month.state].label : 'not released'}`} className={`grid items-center gap-[2px] rounded px-1 py-0.5 text-left disabled:cursor-default ${chosen ? 'bg-marine-lt' : 'hover:bg-canvas'}`} style={{ gridTemplateColumns: '4.5rem repeat(31, minmax(0, 1fr)) 3.5rem' }}>
              <span className={`text-[11px] ${chosen ? 'font-black' : 'font-semibold'} text-ink`}>{shortMonth(month.period)}</span>
              {Array.from({ length: 31 }, (_, index) => {
                const day = month.days[index]
                if (index >= length) {
                  return <span key={index} />
                }
                const paint = !month.released || !day ? heatPaint('none', 'unreleased') : heatPaint(day.state, timeOfDay(day.phase))
                return <span key={index} className="aspect-square min-h-[0.85rem] rounded-[3px]" style={{ background: paint.background, border: day?.phase === 'today' ? '2px solid #0A2830' : day?.phase === 'ahead' ? paint.border.replace('2px', '1px') : 'none' }} title={day ? `${day.date} · ${HEAT_SWATCH[day.state].label} · ${day.staffed} of ${day.required}` : `${month.period}-${String(index + 1).padStart(2, '0')} · not released`} />
              })}
              <span className="text-right text-[11px] font-bold text-ink">{month.released ? pctOf(month.totals.staffed, month.totals.required) : '—'}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export interface MonthHeatmapProps {
  facilityId: string
  period: string
  /** This facility's Dashboard on its Today tab. */
  todayHref: string
  /** Chooses a month from the history strip, as the overview's arrows do. */
  onChoosePeriod: (period: string) => void
}

/**
 * The month's state as a heatmap (26 October 2026, the user's design): the month's verdict, how many days read as each
 * state, the seats worked and run short and the open seats ahead, a calendar of the month - each day coloured by its
 * state and carrying a bar per shift, worked days solid, days ahead striped - with the chosen day's units by shift
 * beside it, and the last twelve months a row each, any released one opened by a tap.
 */
export function MonthHeatmap({ facilityId, period, todayHref, onChoosePeriod }: MonthHeatmapProps) {
  const query = useFacilityHeatmap(facilityId, period)
  const history = useFacilityHeatmapHistory(facilityId)
  const [picked, setPicked] = useState<Record<string, string>>({})
  if (query.isError) {
    return <LoadFailure size="xs" message="Could not load the month’s heatmap." onRetry={() => void query.refetch()} retrying={query.isFetching} />
  }
  const view = query.data
  if (!view || view.period !== period) {
    return <Skeleton lines={6} label="Loading the month’s heatmap" />
  }
  const cells = calendarOf(view.period)
  const selectedDate = picked[view.period] ?? (view.days.some((day) => day.date === view.today) ? view.today : ([...view.days].reverse().find((day) => day.phase === 'worked')?.date ?? view.days[0]?.date))
  const selected = view.days.find((day) => day.date === selectedDate) ?? null
  const verdict = view.released ? heatPaint(view.state, 'worked') : heatPaint('none', 'unreleased')
  const { worked, ahead, month } = view.totals
  const mix = HEAT_ORDER.map((state) => ({ state, count: view.byState[state] ?? 0 })).filter((entry) => entry.count > 0)

  return (
    <section className="flex flex-col gap-4 rounded border border-marine/15 bg-white px-4 py-3.5" aria-label="The month as a heatmap">
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-marine">
          The month at a glance
          <HelpIcon topic="schedule.heatmap" />
        </h2>
        <span className="text-[11px] text-ink-mid">every day&rsquo;s seats held against what its census requires · worked days solid, days ahead striped</span>
      </div>

      {!view.hasStaffingPlan ? (
        <p className="text-xs text-ink-mid">The month is judged against the staffing plan; set one under Configuration › Scheduling › Census.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-stretch gap-4">
            <div className="flex min-h-[7.5rem] w-48 shrink-0 flex-col justify-between gap-2 rounded-lg px-4 py-3" style={paintStyle(verdict)}>
              <span className="text-[10px] font-bold uppercase tracking-wide">The month is</span>
              <span className="text-2xl font-black leading-none">{view.released ? HEAT_SWATCH[view.state].label : 'Not released'}</span>
              <span className="text-xs font-semibold">{view.released ? `${month.staffed} of ${month.required} seats · ${pctOf(month.staffed, month.required)}` : 'Nothing to judge until it is released'}</span>
            </div>
            <div className="flex min-w-0 flex-1 basis-80 flex-col gap-3">
              {mix.length > 0 && (
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">Days by state</span>
                  <div className="flex h-4 gap-[2px] overflow-hidden rounded" role="img" aria-label={mix.map((entry) => `${HEAT_SWATCH[entry.state].label} ${entry.count}`).join(', ')}>
                    {mix.map((entry) => (
                      <span key={entry.state} style={{ flex: `${entry.count} 1 0`, background: HEAT_SWATCH[entry.state].fill }} />
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-3 text-[11px] text-ink-mid">
                    {mix.map((entry) => (
                      <span key={entry.state}>
                        {HEAT_SWATCH[entry.state].label} · {entry.count} {entry.count === 1 ? 'day' : 'days'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {view.released && (
                <div className="flex flex-wrap gap-2">
                  <Tile label="Seats worked" value={worked.required > 0 ? pctOf(worked.staffed, worked.required) : '—'} note={worked.required > 0 ? `${worked.staffed} of ${worked.required} required so far` : 'No day worked yet'} />
                  <Tile label="Run short" value={String(worked.ranShort + worked.uncovered)} note="seats nobody covered" />
                  <Tile label="Open ahead" value={ahead.required > 0 ? String(ahead.required - ahead.staffed) : '—'} note={ahead.required > 0 ? `${ahead.onBoard} on the board` : 'No days ahead'} />
                  <Tile label="Critical days" value={String(view.byState.critical ?? 0)} note={`under ${Math.round(view.scale.short * 100)}% of seats held`} />
                </div>
              )}
            </div>
          </div>

          <div className="grid items-start gap-4 lg:grid-cols-[3fr_1.3fr]">
            <div className="flex flex-col gap-1">
              <div className="grid grid-cols-7 gap-1">
                {WEEKDAYS.map((weekday) => (
                  <span key={weekday} className="px-1 text-[10px] font-bold uppercase tracking-wide text-ink-mid">
                    {weekday}
                  </span>
                ))}
                {cells.map((cell, index) => {
                  if (!cell) {
                    return <span key={`blank-${index}`} aria-hidden="true" />
                  }
                  const day = view.days.find((entry) => entry.date === cell.date)
                  if (!day) {
                    return (
                      <span key={cell.date} className="flex min-h-[4.75rem] rounded-md px-1.5 py-1 text-sm font-black" style={paintStyle(heatPaint('none', 'unreleased'))}>
                        {cell.day}
                      </span>
                    )
                  }
                  return <DayCell key={cell.date} day={day} scale={view.scale} selected={day.date === selectedDate} onSelect={() => setPicked({ ...picked, [view.period]: day.date })} />
                })}
              </div>
              <p className="pt-1 text-[11px] text-ink-mid">The three bars in each day are its shifts: day, evening and night.</p>
            </div>
            <div className="rounded border border-marine/15 px-3 py-3">
              {selected ? <DayDetail day={selected} view={view} todayHref={todayHref} /> : <p className="text-xs text-ink-mid">{view.released ? 'Choose a day.' : 'The month is not released, so no day holds anyone yet.'}</p>}
            </div>
          </div>
          <HeatLegend unreleased={!view.released || (history.data?.months.some((entry) => !entry.released) ?? false)} />
        </>
      )}

      <div className="flex flex-col gap-2 border-t border-marine/10 pt-3">
        <div className="flex flex-wrap items-baseline gap-2">
          <h3 className="text-[11px] font-bold uppercase tracking-wide text-marine">The last twelve months</h3>
          <span className="text-[11px] text-ink-mid">one row a month, one square a day · tap a month to open it</span>
        </div>
        {history.isError ? (
          <LoadFailure size="xs" message="Could not load the months before." onRetry={() => void history.refetch()} retrying={history.isFetching} />
        ) : history.data ? (
          <HistoryStrip months={history.data.months} current={view.period} onChoose={onChoosePeriod} />
        ) : (
          <Skeleton lines={4} label="Loading the last twelve months" />
        )}
      </div>
    </section>
  )
}

